import type { Dayjs } from 'dayjs';
import type { ConfirmClientReceiptBody } from '../types';
import type { ClientReceiptResult, ClosingPackageOriginalsProblemType } from './originalsProblemTypes';
import { isCommentRequired, trimOptionalComment } from './commentPolicy';
import {
  validateTaskModalEventDate,
  validateTaskModalRequiredComment,
} from './taskModalValidation';

export type ClientReceiptValidationCode =
  | 'EVENT_DATE_REQUIRED'
  | 'COMMENT_REQUIRED'
  | 'PROBLEM_TYPE_REQUIRED';

export type ClientReceiptValidationIssue = {
  code: ClientReceiptValidationCode;
  message: string;
};

export type ValidateClientReceiptFormInput = {
  result: ClientReceiptResult;
  eventAt: Dayjs | null | undefined;
  problemType?: ClosingPackageOriginalsProblemType | null;
  comment: string | null | undefined;
};

export function validateClientReceiptSubmit(
  input: ValidateClientReceiptFormInput
): ClientReceiptValidationIssue | null {
  const dateIssue = validateTaskModalEventDate(
    input.eventAt,
    input.result === 'PROBLEM' ? 'Укажите дату проверки' : 'Укажите дату подтверждения'
  ) as ClientReceiptValidationIssue | null;

  if (dateIssue) {
    return dateIssue;
  }

  if (input.result === 'PROBLEM') {
    if (!input.problemType) {
      return {
        code: 'PROBLEM_TYPE_REQUIRED',
        message: 'Укажите тип проблемы',
      };
    }

    return validateTaskModalRequiredComment(
      input.comment,
      'Укажите комментарий'
    ) as ClientReceiptValidationIssue | null;
  }

  return null;
}

export function buildClientReceiptBody(input: {
  result: ClientReceiptResult;
  comment: string | null | undefined;
  receivedAt: string;
  problemType?: ClosingPackageOriginalsProblemType | null;
}): ConfirmClientReceiptBody {
  const body: ConfirmClientReceiptBody = {
    result: input.result,
    confirmationSource: 'PHONE',
    receivedAt: input.receivedAt,
  };

  const trimmedComment = trimOptionalComment(input.comment);
  if (trimmedComment) {
    body.comment = trimmedComment;
  }

  if (input.result === 'PROBLEM' && input.problemType) {
    body.problemType = input.problemType;
  }

  return body;
}

export function isClientReceiptCommentRequired(result: ClientReceiptResult | null | undefined): boolean {
  return isCommentRequired(result);
}

export function getClientReceiptSuccessMessage(result: ClientReceiptResult): string {
  return result === 'PROBLEM'
    ? 'Проблема с получением зарегистрирована — создана задача подготовки оригиналов'
    : 'Получение оригиналов подтверждено';
}

export function getClientReceiptSubmitLabel(result: ClientReceiptResult): string {
  return result === 'PROBLEM' ? 'Зарегистрировать проблему' : 'Подтвердить получение';
}
